'use client'

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react'
import { Resolver, useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import Flag from 'react-flagkit'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
  BuildingIcon,
  LanguagesIcon,
  MapPinIcon,
  PhoneIcon,
  MailIcon,
  PrinterIcon,
  PlusCircleIcon,
  SaveIcon,
  XIcon,
  PlusIcon
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { NumberInput } from '@/components/ui/number-input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { useMetaStore } from '@/store'
import {
  CreateContactUnitParams,
  ContactUnitTranslationParam,
  ContactUnitTranslationsParam,
  ContactUnitType,
  ContactUnitEmailType
} from '@/types/contact-unit'
import { LoadingIndicator, UnsavedChangesGuardDialog } from '@/components/partials'
import { FormModeEnum } from '@/enums'
import { setValidationErrors } from '@/lib/utils'
import { useScrollToError, useUnsavedChangesGuard } from '@/hooks'
import { SortablePhoneItem } from './sortable-phone-item'
import { SortableFaxItem } from './sortable-fax-item'
import { SortableEmailItem } from './sortable-email-item'

type PropsType = {
  initialData?: ContactUnitType | null
  onSubmit: (data: CreateContactUnitParams) => void
  onSubmitAndNew?: (data: CreateContactUnitParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type ContactUnitFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

type FormData = {
  address: string
  latitude: number | null
  longitude: number | null
  phones: { value: string }[]
  faxes: { value: string }[]
  emails: ContactUnitEmailType[]
  translations: ContactUnitTranslationsParam
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const ContactUnitForm = forwardRef<ContactUnitFormRef, PropsType>(
  ({ initialData, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const { scrollToFirstError } = useScrollToError()

    const { metaState } = useMetaStore()
    const locales = useMemo(() => metaState.response?.locales || [], [metaState.response])

    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    const getCountryCodeFromLocale = (localeCode: string): string => {
      const parts = localeCode.split('-')
      return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
    }

    const hasLocaleError = (localeCode: string, field: keyof ContactUnitTranslationParam): boolean => {
      const translationErrors = formErrors.translations as Record<string, Record<string, unknown>> | undefined
      return !!translationErrors?.[localeCode]?.[field]
    }

    const schema = useMemo(() => {
      const translationsSchema: Record<string, z.ZodType<ContactUnitTranslationParam>> = {}
      locales.forEach(locale => {
        translationsSchema[locale.fullCode] = z.object({
          title: z.string().min(1, t('required'))
        })
      })

      return z.object({
        address: z.string().min(1, t('required')),
        latitude: z
          .union([
            z.literal('').transform(() => null),
            z.coerce
              .number()
              .min(-90, t('min_value', { value: -90 }))
              .max(90, t('max_value', { value: 90 }))
          ])
          .nullable(),
        longitude: z
          .union([
            z.literal('').transform(() => null),
            z.coerce
              .number()
              .min(-180, t('min_value', { value: -180 }))
              .max(180, t('max_value', { value: 180 }))
          ])
          .nullable(),
        phones: z
          .array(z.object({ value: z.string().min(1, t('required')) }))
          .optional()
          .default([]),
        faxes: z
          .array(z.object({ value: z.string().min(1, t('required')) }))
          .optional()
          .default([]),
        emails: z
          .array(
            z.object({
              name: z.string().min(1, t('required')),
              email: z
                .string()
                .min(1, t('required'))
                .trim()
                .transform(val => val.toLowerCase())
                .refine(val => EMAIL_REGEX.test(val), { message: t('invalid_email') })
            })
          )
          .optional()
          .default([]),
        translations: z.object(translationsSchema)
      })
    }, [locales, t])

    const getDefaultValues = useCallback((): FormData => {
      const defaultTranslations: ContactUnitTranslationsParam = {}
      locales.forEach(locale => {
        const translation = initialData?.translations?.[locale.fullCode]
        defaultTranslations[locale.fullCode] = { title: translation?.title ?? '' }
      })

      return {
        address: initialData?.address ?? '',
        latitude: initialData?.latitude ?? null,
        longitude: initialData?.longitude ?? null,
        phones: initialData?.phones?.map(p => ({ value: p })) ?? [],
        faxes: initialData?.faxes?.map(f => ({ value: f })) ?? [],
        emails: initialData?.emails ?? [],
        translations: defaultTranslations
      }
    }, [locales, initialData])

    const form = useForm<FormData>({
      resolver: zodResolver(schema) as Resolver<FormData>,
      defaultValues: getDefaultValues(),
      mode: 'onChange'
    })

    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    const {
      fields: phoneFields,
      append: appendPhone,
      remove: removePhone,
      move: movePhone
    } = useFieldArray({
      control: form.control,
      name: 'phones'
    })

    const {
      fields: faxFields,
      append: appendFax,
      remove: removeFax,
      move: moveFax
    } = useFieldArray({
      control: form.control,
      name: 'faxes'
    })

    const {
      fields: emailFields,
      append: appendEmail,
      remove: removeEmail,
      move: moveEmail
    } = useFieldArray({
      control: form.control,
      name: 'emails'
    })

    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
        scrollToFirstError()
      },
      reset: () => form.reset()
    }))

    useEffect(() => {
      if (locales.length > 0) {
        form.reset(getDefaultValues())
      }
    }, [getDefaultValues, locales, form])

    const formErrors = form.formState.errors

    const handleDragEnd =
      (fields: { id: string }[], move: (from: number, to: number) => void) => (event: DragEndEvent) => {
        const { active, over } = event
        if (over && active.id !== over.id) {
          const oldIndex = fields.findIndex(field => field.id === active.id)
          const newIndex = fields.findIndex(field => field.id === over.id)
          if (oldIndex !== -1 && newIndex !== -1) {
            move(oldIndex, newIndex)
          }
        }
      }

    const handleFormSubmit = (data: FormData) => {
      const submissionData: CreateContactUnitParams = {
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        phones: data.phones?.map(p => p.value).filter(Boolean),
        faxes: data.faxes?.map(f => f.value).filter(Boolean),
        emails: data.emails?.filter(e => e.name && e.email),
        translations: {}
      }

      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          title: data.translations[locale.fullCode]?.title || ''
        }
      })

      onSubmit(submissionData)
    }

    const handleFormSubmitAndNew = (data: FormData) => {
      const submissionData: CreateContactUnitParams = {
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        phones: data.phones?.map(p => p.value).filter(Boolean),
        faxes: data.faxes?.map(f => f.value).filter(Boolean),
        emails: data.emails?.filter(e => e.name && e.email),
        translations: {}
      }

      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          title: data.translations[locale.fullCode]?.title || ''
        }
      })

      onSubmitAndNew?.(submissionData)
    }

    if (!metaState.response?.locales) {
      return <LoadingIndicator />
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(
            isCreateModeWithSubmitAndNew ? handleFormSubmitAndNew : handleFormSubmit,
            scrollToFirstError
          )}
          className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BuildingIcon className="h-5 w-5" />
                {mode === FormModeEnum.CREATE ? t('create_contact_unit') : t('edit_contact_unit')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Translations */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <LanguagesIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">{t('translations')}</h3>
                </div>

                {locales.length > 0 ? (
                  <Tabs defaultValue={locales[0].fullCode} className="w-full">
                    <TabsList className="min-h-10.5 w-full">
                      {locales.map(locale => {
                        const hasError = hasLocaleError(locale.fullCode, 'title')
                        return (
                          <TabsTrigger
                            key={locale.fullCode}
                            value={locale.fullCode}
                            className={`relative flex items-center gap-2 ${hasError ? 'text-destructive border-destructive' : ''}`}>
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={16} />
                            <span className="hidden sm:inline">{locale.name}</span>
                            {hasError && (
                              <div className="bg-destructive absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full" />
                            )}
                          </TabsTrigger>
                        )
                      })}
                    </TabsList>

                    {locales.map(locale => {
                      const hasTitleError = hasLocaleError(locale.fullCode, 'title')
                      return (
                        <TabsContent
                          key={locale.fullCode}
                          value={locale.fullCode}
                          forceMount
                          className="mt-6 space-y-4 data-[state=inactive]:hidden">
                          <div className="flex items-center gap-2">
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={20} />
                            <h4 className="text-lg font-semibold">{locale.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {locale.fullCode}
                            </Badge>
                            {hasTitleError && (
                              <Badge variant="destructive" className="animate-pulse text-xs">
                                {t('error')}
                              </Badge>
                            )}
                          </div>

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.title`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel required>{t('title')}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('title')}
                                    className={hasTitleError ? 'border-destructive focus:border-destructive' : ''}
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      )
                    })}
                  </Tabs>
                ) : (
                  <div className="py-8 text-center">
                    <p>{t('no_locales_available')}</p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Location Details */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <MapPinIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">{t('location')}</h3>
                </div>

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('address')}</FormLabel>
                      <FormControl>
                        <Textarea placeholder={t('address')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="latitude"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('latitude')}</FormLabel>
                        <FormControl>
                          <NumberInput
                            placeholder={t('latitude')}
                            min={-90}
                            max={90}
                            decimalScale={6}
                            stepper={0.000001}
                            value={field.value ?? undefined}
                            onValueChange={v => field.onChange(v ?? null)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="longitude"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('longitude')}</FormLabel>
                        <FormControl>
                          <NumberInput
                            placeholder={t('longitude')}
                            min={-180}
                            max={180}
                            decimalScale={6}
                            stepper={0.000001}
                            value={field.value ?? undefined}
                            onValueChange={v => field.onChange(v ?? null)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              <div className="flex w-full flex-row flex-wrap items-start gap-5">
                {/* Phones */}
                <div className="min-w-75 flex-1 space-y-4 border p-5">
                  <div className="flex items-center justify-between gap-5 rounded-md">
                    <div className="flex items-center gap-2">
                      <PhoneIcon className="h-5 w-5" />
                      <h3 className="text-lg font-semibold">{t('phones')}</h3>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => appendPhone({ value: '' })}>
                      <PlusIcon className="h-4 w-4" />
                      {t('add')}
                    </Button>
                  </div>

                  {phoneFields.length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd(phoneFields, movePhone)}>
                      <SortableContext items={phoneFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2">
                          {phoneFields.map((field, index) => (
                            <FormField
                              key={field.id}
                              control={form.control}
                              name={`phones.${index}.value`}
                              render={({ field: formField }) => (
                                <FormItem>
                                  <FormControl>
                                    <SortablePhoneItem
                                      id={field.id}
                                      value={formField.value || ''}
                                      onChange={formField.onChange}
                                      onRemove={() => removePhone(index)}
                                      placeholder={t('phone')}
                                      error={formErrors.phones?.[index]?.value?.message}
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>

                {/* Faxes */}
                <div className="min-w-[300px] flex-1 space-y-4 border p-5">
                  <div className="flex items-center justify-between gap-5 rounded-md">
                    <div className="flex items-center gap-2">
                      <PrinterIcon className="h-5 w-5" />
                      <h3 className="text-lg font-semibold">{t('faxes')}</h3>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => appendFax({ value: '' })}>
                      <PlusIcon className="h-4 w-4" />
                      {t('add')}
                    </Button>
                  </div>

                  {faxFields.length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd(faxFields, moveFax)}>
                      <SortableContext items={faxFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2">
                          {faxFields.map((field, index) => (
                            <FormField
                              key={field.id}
                              control={form.control}
                              name={`faxes.${index}.value`}
                              render={({ field: formField }) => (
                                <FormItem>
                                  <FormControl>
                                    <SortableFaxItem
                                      id={field.id}
                                      value={formField.value || ''}
                                      onChange={formField.onChange}
                                      onRemove={() => removeFax(index)}
                                      placeholder={t('fax')}
                                      error={formErrors.faxes?.[index]?.value?.message}
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>

                {/* Emails */}
                <div className="min-w-[300px] flex-1 space-y-4 border p-5">
                  <div className="flex items-center justify-between gap-5 rounded-md">
                    <div className="flex items-center gap-2">
                      <MailIcon className="h-5 w-5" />
                      <h3 className="text-lg font-semibold">{t('emails')}</h3>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => appendEmail({ name: '', email: '' })}>
                      <PlusIcon className="h-4 w-4" />
                      {t('add')}
                    </Button>
                  </div>

                  {emailFields.length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd(emailFields, moveEmail)}>
                      <SortableContext items={emailFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                        <div className="c space-y-4">
                          {emailFields.map((field, index) => (
                            <FormField
                              key={field.id}
                              control={form.control}
                              name={`emails.${index}.name`}
                              render={({ field: nameField }) => (
                                <FormField
                                  control={form.control}
                                  name={`emails.${index}.email`}
                                  render={({ field: emailField }) => (
                                    <FormItem>
                                      <FormControl>
                                        <SortableEmailItem
                                          id={field.id}
                                          index={index}
                                          name={nameField.value || ''}
                                          email={emailField.value || ''}
                                          onNameChange={nameField.onChange}
                                          onEmailChange={emailField.onChange}
                                          onRemove={() => removeEmail(index)}
                                          labels={{ email: t('email'), name: t('name') }}
                                          nameError={formErrors.emails?.[index]?.name?.message}
                                          emailError={formErrors.emails?.[index]?.email?.message}
                                        />
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />
                              )}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </div>

              <Separator />

              {/* Form actions */}
              <div className="flex items-center justify-between gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading || form.formState.isSubmitting}>
                  <XIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => form.handleSubmit(handleFormSubmit, scrollToFirstError)()}
                      disabled={isLoading || form.formState.isSubmitting}>
                      <SaveIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">
                        {isLoading || form.formState.isSubmitting ? t('saving') : t('save')}
                      </span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading || form.formState.isSubmitting}>
                    {isCreateModeWithSubmitAndNew ? (
                      <PlusCircleIcon className="h-4 w-4" />
                    ) : (
                      <SaveIcon className="h-4 w-4" />
                    )}
                    {isLoading || form.formState.isSubmitting
                      ? t('saving')
                      : isCreateModeWithSubmitAndNew
                        ? t('save_and_add_new')
                        : t(mode)}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <UnsavedChangesGuardDialog
            open={isNavigationBlocked}
            onConfirm={confirmNavigation}
            onCancel={cancelNavigation}
          />
        </form>
      </Form>
    )
  }
)

ContactUnitForm.displayName = 'ContactUnitForm'

export default ContactUnitForm
